// Menciones de WhatsApp en grupos: el texto trae "@<número>" (el id interno
// de la persona) y WhatsApp muestra su nombre. El backend ya guarda los
// mensajes nuevos con el nombre; esto cubre los anteriores usando los
// nombres que la propia conversación conoce de cada participante.

const parsePayload = (raw) => {
  if (typeof raw !== "string") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/** Dígitos de cada JID mencionado ("124030166282485@lid" → "124030166282485"). */
export function mentionedDigits(msg) {
  try {
    const raw = parsePayload(msg?.raw_payload);
    if (!raw || typeof raw !== "object") return [];
    const lists = [raw.contextInfo?.mentionedJid];
    if (raw.message && typeof raw.message === "object") {
      for (const value of Object.values(raw.message)) lists.push(value?.contextInfo?.mentionedJid);
    }
    const out = new Set();
    for (const list of lists) {
      if (!Array.isArray(list)) continue;
      for (const jid of list) {
        const m = typeof jid === "string" && /^(\d+)@(lid|s\.whatsapp\.net)$/.exec(jid);
        if (m) out.add(m[1]);
      }
    }
    return [...out];
  } catch {
    return [];
  }
}

// "." o "🔇" no sirven como nombre: exige al menos una letra.
const isDisplayableName = (name) => typeof name === "string" && /\p{L}/u.test(name);

/**
 * Devuelve los mensajes con las menciones reemplazadas por "@Nombre" y, si
 * el nombre de WhatsApp del remitente no tiene letras, con su último nombre
 * legible en la conversación. Los
 * mensajes sin cambios conservan su identidad (las filas memoizadas no se
 * re-renderizan) y sin menciones que resolver devuelve el mismo arreglo.
 */
export function withGroupMentionNames(messages) {
  if (!Array.isArray(messages) || !messages.length) return messages;
  // Nombre por participante: el último con letras que usó en la conversación.
  const names = new Map();
  for (const m of messages) {
    const lid = typeof m?.participant_lid === "string" ? m.participant_lid.split("@")[0] : null;
    if (lid && isDisplayableName(m.sender_name)) names.set(lid, m.sender_name.trim());
    const phone = m?.participant_phone ? String(m.participant_phone) : null;
    if (phone && isDisplayableName(m.sender_name)) names.set(phone, m.sender_name.trim());
  }
  if (!names.size) return messages;
  let changed = false;
  const out = messages.map((m) => {
    // Remitente sin letras ("🔇", "."): el último nombre con letras que usó.
    const ownLid = typeof m?.participant_lid === "string" ? m.participant_lid.split("@")[0] : null;
    const betterSender =
      !isDisplayableName(m?.sender_name) &&
      ((ownLid && names.get(ownLid)) ||
        (m?.participant_phone && names.get(String(m.participant_phone))));
    if (betterSender) {
      changed = true;
      m = { ...m, sender_name: betterSender };
    }
    if (typeof m?.content !== "string" || !m.content.includes("@")) return m;
    const digits = mentionedDigits(m).filter((d) => names.has(d));
    if (!digits.length) return m;
    const content = m.content.replace(/@(\d{6,})(?!\d)/g, (match, d) =>
      digits.includes(d) ? `@${names.get(d)}` : match,
    );
    if (content === m.content) return m;
    changed = true;
    return { ...m, content };
  });
  return changed ? out : messages;
}
